import { useMemo } from "react"
import type { WorkerProfile, WorkerScenario } from "@/components/ats-fbo/types"
import { useTranslation } from "@/hooks/use-translation"
import { usePendingLanguageChange } from "@/hooks/use-pending-language-change"

/**
 * Encapsulates the Preferred Language section's control logic so the JSX in
 * worker-setup-section.tsx stays presentational. Wraps three concerns:
 *
 *  1. Mode gate. The section is "readonly" as soon as the email-based FBO search
 *     returns at least one matching Worker profile (Contact.CultureId is canonical
 *     for an existing contact); "editable" otherwise.
 *  2. FBO contact deep-link resolution. When read-only, picks the right Worker
 *     profile to land on in FBO based on the current scenario, falling back to
 *     workerAllProfiles[0] when no scenario is picked yet.
 *  3. Pending-change confirmation gate (delegated to usePendingLanguageChange).
 *
 * Pure data + handler hook — no JSX. Pill components and warning composition stay
 * in the component since they need <span> markup with classes.
 */
export interface UsePreferredLanguageProps {
  workerScenario: WorkerScenario | null
  workerAllProfiles: WorkerProfile[]
  /** Form-state's normalized culture code, e.g. "en-CA" or "fr-CA". Used as the
   *  request-side value for mismatch detection and as the fallback display value
   *  when FBO has no CultureId set. */
  currentValue: string
  /** Called when the user confirms a pending change (editable mode only). */
  onCommit: (next: string) => void
  /** Base FBO web URL — when falsy the deep-link is suppressed and only the
   *  message renders in the read-only branch. */
  fboAppUrl?: string
}

export interface UsePreferredLanguageReturn {
  mode: "readonly" | "editable"
  /** Value to render in the disabled / editable dropdown. */
  displayCode: string
  /** Two-option list for the SearchableSelect. Translated once per render. */
  languageOptions: { id: string; name: string }[]
  /** Resolves a culture code to its display label (e.g. "en-CA" → "English"). */
  labelFor: (code: string | null) => string

  // ---- Read-only branch fields ----
  fboLink: string | null
  /** FBO's stored culture code for this contact, or null when FBO has no value. */
  fboCultureCode: string | null
  /** The request-side value (form state) — used for mismatch comparison. */
  requestCultureCode: string
  /** True when both fboCultureCode and requestCultureCode are set and disagree. */
  hasMismatch: boolean

  // ---- Editable branch fields ----
  /** Value awaiting user confirmation; null when no change is in flight. */
  pendingValue: string | null
  /** Dropdown picks route through here — buffers the change until confirm/cancel. */
  attemptChange: (next: string) => void
  /** Commit the pending value (calls onCommit). */
  confirm: () => void
  /** Discard the pending value. */
  cancel: () => void
}

export function usePreferredLanguage({
  workerScenario,
  workerAllProfiles,
  currentValue,
  onCommit,
  fboAppUrl,
}: UsePreferredLanguageProps): UsePreferredLanguageReturn {
  const { t } = useTranslation()
  const { pendingValue, attemptChange, confirm, cancel } = usePendingLanguageChange({
    currentValue,
    onCommit,
  })

  const languageOptions = useMemo(
    () => [
      { id: "en-CA", name: t("languageEnglish") },
      { id: "fr-CA", name: t("languageFrench") },
    ],
    [t],
  )

  const labelFor = (code: string | null): string =>
    code === "fr-CA" ? t("languageFrench")
      : code === "en-CA" ? t("languageEnglish")
        : code ?? ""

  const mode: "readonly" | "editable" =
    workerAllProfiles.length > 0 ? "readonly" : "editable"

  // FBO contact deep-link target: prefer the user's selected scenario; otherwise
  // fall back to the first profile so the link still resolves before a worker pick.
  const linkProfile = workerScenario?.kind === "existing-profile"
    ? workerAllProfiles.find((p) => p.id === workerScenario.profileId)
    : workerScenario?.kind === "add-profile"
      ? workerAllProfiles.find((p) => p.contactId === workerScenario.contactId)
      : workerAllProfiles[0]

  const fboLink = fboAppUrl && linkProfile
    ? `${fboAppUrl}/next/contact/${linkProfile.contactId}/profile/${linkProfile.profileTypeCode}/${linkProfile.id}/details`
    : null

  // Contact.CultureId is contact-level, so any returned profile's culture works.
  // Pick the first non-null to tolerate mixed nulls in the list.
  const fboCultureCode = workerAllProfiles
    .map((p) => p.preferredLanguage)
    .find((c): c is string => !!c) ?? null

  const requestCultureCode = currentValue
  const hasMismatch = fboCultureCode !== null && fboCultureCode !== requestCultureCode
  const displayCode = mode === "readonly" ? (fboCultureCode ?? currentValue) : currentValue

  return {
    mode,
    displayCode,
    languageOptions,
    labelFor,
    fboLink,
    fboCultureCode,
    requestCultureCode,
    hasMismatch,
    pendingValue,
    attemptChange,
    confirm,
    cancel,
  }
}
